<template>
	<section class="container page-content">
		<div class="row">
			<div class="col-xs-12 col-sm-8">
				<template v-if="searchTerm">
					<h1>Search results</h1>
					<div class="page-summary-container">
						<div class="page-summary-text search-term">
							{{ `"${searchTerm}"` }} <small>({{regulations.length}} results)</small>
							<b-btn
								class="btn-primary pull-right"
								@click="
									setSearchTerm(null);
									search();
								"
							>
								Reset
							</b-btn>
						</div>
					</div>
				</template>
				<template v-else>
					<h1>Project Database</h1>
					<div class="page-summary-container">
						<div class="page-summary-text">Explore all available projects.</div>
						<div class="page-summary-text"></div>
					</div>
				</template>
			</div>
			<div class="col-xs-6 col-sm-3 col-sm-offset-1">
				<b-btn
					class="btn btn-primary btn-md btn-block btn-filter"
					data-toggle="collapse"
					aria-expanded="false"
					@click="showFilters = !showFilters"
				>
					Sort and filter
				</b-btn>
			</div>
		</div>
		<div class="row">
			<div class="col-xs-12">
				<filtering
					:showFilters="showFilters"
					:filter-options="filterOptions"
					:sort-options="sortOptions"
					:selected-filters="selectedFilters"
					@setSearchTerm = "setSearchTerm(term)"
					@resetSearchTerm="setSearchTerm(null)"
					@selectedOptions="applyFilters"
				/>
			</div>
		</div>
		<template v-if="loading">
			<div class="row">
				<div class="col-xs-12">
					<div class="container loading-container">
						<img
							class="section-loader"
							src="@/assets/images/fse_reg-section-loader-admin.gif"
						/>
					</div>
				</div>
			</div>
		</template>
		<template v-else-if="regulations && regulations.length > 0">
			<div
				v-for="regulation in regulations"
				:key="'reg' + regulation.id"
				class="list-container list-container-notifications"
			>
				<div class="row">
					<div class="col-xs-12 col-sm-12 col-md-3 col-lg-2">
						<div class="sm-padding-bottom-10">
							<template v-if="regulation.clients.length > 0">
								<b-btn
									type="default"
									v-for="client in regulation.clients"
									:key="'c' + client.id"
									class="label label-success"
									@click="appendAndApplyFilter('clients', client.id)"
								>
									{{ client.name }}
								</b-btn>
							</template>
							<template v-else>
								<div class="sm-padding-bottom-10">
									<span class="label label-default">No clients set</span>
								</div>
							</template>
							<div>
								{{ formatStartDate(regulation.start_date) }}
							</div>
						</div>
					</div>
					<div class="col-xs-12 col-sm-12 col-md-6 col-lg-7">
						<div class="sm-padding-bottom-10">
							<div class="notification-list-heading">
								<router-link :to="`/project/${regulation.id}`">
									{{ regulation.title }}
								</router-link>
							</div>
						</div>
					</div>
					<div class="col-xs-12 col-sm-12 col-md-3 col-lg-3">
						<div>
							<b-btn
								v-for="region in regulation.regions"
								:key="'r'+region.id"
								class="label label-info"
								@click="appendAndApplyFilter('regions', region.id)"
							>
								&nbsp;{{ region.name }}&nbsp;
							</b-btn>
							<br />
							<!--
							<b-btn
								v-for="tag in regulation.tags"
								:key="'t'+tag.id"
								class="label label-secondary"
								@click="appendAndApplyFilter('tags', tag.id)"
							>
								&nbsp;{{ tag.name }}&nbsp;
							</b-btn>
							-->
						</div>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="row">
				<div class="col-xs-12">
					<div class="container center">
						<h2>No results found</h2>
					</div>
				</div>
			</div>
		</template>
		<div class="row">
			<div class="col-xs-12">
				<div class="pagination-container">
					<b-pagination
						v-if="pagination && pagination.totalPages > 1"
						v-model="pagination.page"
						:total-page="pagination.totalPages"
					/>
				</div>
			</div>
		</div>
	</section>
</template>

<style lang="less" scoped>
.input-group {
	margin-bottom: 30px;
}
.btn-primary {
	height: 40.25px;
}
.loading-container {
	text-align: center;
}
.section-loader {
	max-width: 200px;
	height: auto;
	margin: 30px auto;
}
</style>

<script>
import moment from 'moment';
import filtering from '@/components/filtering';
import { map, isEmpty, isNull, flow, filter, orderBy } from 'lodash/fp';

export default {
	components: { filtering },
	created() {
		this.setPreSelectedFilters();
		this.getRegulations();
		this.getRegulationFilterOptions();
	},
	data() {
		return {
			showFilters: false,
			regulations: null,
			filterOptions: {},
			sortOptions: [],
			pagination: {
				page: 1,
			},
			searchTerm: null,
			selectedSortOrder: null,
			selectedFilters: {
				searchTerm: '',
				clients: [],
				status: [],
				regions: [],
				dates: [],
				dateFrom: '',
				dateTo: '',
				tags: [],
			},
			loading: false,
		};
	},
	computed: {
		getSearchTermFromQuery() {
			return this.$route.query.searchTerm;
		},
	},
	methods: {
		setPreSelectedFilters() {
			if (!isEmpty(this.$route.query.selectedFilters)) {
				this.$set(this, 'selectedFilters', this.$route.query.selectedFilters);
			}
			if (!isNull(this.$route.query.searchTerm)) {
				this.setSearchTerm(this.$route.query.searchTerm);
			}
		},
		formatStartDate(startDate) {
			const date = moment(startDate).format('DD/MM/YYYY');

			if (date === 'Invalid date') {
				return 'In progress';
			}
			return `Completed: ${date}`;
		},
		async getRegulations(params) {
			this.loading = true;
			const { searchTerm, selectedSortOrder, selectedFilters } = this;

			const {
				data: { data: regulations, pagination },
			} = await this.$axios.get('/resource', {
				params: {
					searchTerm,
					sortBy: selectedSortOrder,
					page: this.pagination.page,
					...selectedFilters,
					...params,
				},
			});
			this.$set(this, 'regulations', regulations);
			this.$set(this, 'pagination', pagination);
			this.loading = false;
		},
		async getRegulationFilterOptions() {
			const {
				data: { data: options },
			} = await this.$axios.get('/resource/filter-options');

			const formattedSortOptions = map(
				({ code, name }) => ({
					value: code,
					label: name,
				}),
				options.sortOptions
			);

			const formattedClients = flow(
				map(({ id, name }) => ({
					value: id,
					label: name,
				})),
				orderBy('label', 'asc')
			)(options.filterOptions.clients);

			const formattedRegions = flow(
				map(({ id, name }) => ({
					value: id,
					label: name,
				})),
				orderBy('label', 'asc')
			)(options.filterOptions.regions);

			const formattedStatus = map(
				({ id, name }) => ({
					value: id,
					label: name,
				}),
				options.filterOptions.status
			);

			const formattedDates = flow(
				filter(value => value !== 'Invalid date'),
				map(value => ({ value, label: value }))
			)(options.filterOptions.dates);

			const formattedTags = map(
				({ id, name }) => ({
					value: id,
					label: name,
				}),
				options.filterOptions.tags
			);

			this.$set(this, 'sortOptions', formattedSortOptions);
			this.$set(this, 'filterOptions', {
				clients: formattedClients,
				regions: formattedRegions,
				status: formattedStatus,
				dates: formattedDates,
				tags: formattedTags,
				dateFrom: '',
				dateTo: '',
			});
		},
		async applyFilters(options) {
			this.$set(this, 'searchTerm', options.searchTerm);
			this.$set(this, 'selectedSortOrder', options.selectedSortOrder);
			this.$set(this, 'selectedFilters', options.selectedFilters);
			await this.getRegulations({ page: 1 });
		},
		async search() {
			await this.getRegulations({ page: 1 });
		},
		setSearchTerm(term) {
			this.$set(this, 'searchTerm', term);
		},
		resetFilters() {
			this.searchTerm = '';
			this.selectedFilters = {
				regions: [],
				clients: [],
				status: [],
				dates: [],
				tags: [],
			};
		},
		async appendAndApplyFilter(type, id) {
			this.resetFilters();
			this.$set(this.selectedFilters, type, [id]);
			await this.getRegulations({ page: 1 });
		},
	},
	watch: {
		'pagination.page': async function pageWatcher() {
			await this.getRegulations();
		},
		'$route.query.searchTerm': async function searchTermWatcher() {
			if (!isNull(this.$route.query.searchTerm)) {
				this.setSearchTerm(this.$route.query.searchTerm);
				await this.search();
			} else {
				await this.getRegulations();
			}
		},
	},
};
</script>
